import {FC, useCallback, useEffect, useState} from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import {
	Alert,
	AlertTitle,
	Box,
	FormControlLabel,
	Radio,
	RadioGroup,
	Stack,
	Typography
} from "@mui/material";
import type {Dayjs} from "dayjs";
import {makeStyles} from "tss-react/mui";
import {palette} from "@/constants";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {CourseEnrollmentDueDateSection} from "./CourseEnrollmentDueDateSection";

const buttonSx = {
	border: "none",
	boxShadow: "none",
	color: "primary.main",
	"&:hover": {
		backgroundColor: palette.orange08
	}
};

const useStyles = makeStyles()((theme) => ({
	optionBox: {
		padding: theme.spacing(2),
		borderWidth: theme.borderWidth.small,
		borderColor: theme.palette.grey[100],
		borderStyle: "solid",
		borderRadius: theme.borderRadius.small
	},
	optionBoxSelected: {
		borderColor: theme.palette.primary.main,
		borderWidth: theme.borderWidth.medium
	},
	optionTitle: {
		fontWeight: theme.typography.fontWeightBold
	},
	optionDescription: {
		color: theme.palette.text.secondary
	},
	subtitle: {
		color: theme.palette.text.secondary,
		marginTop: theme.spacing(0.5)
	}
}));

export type ManageDueDatesMode = "set" | "clear";

export interface ManageDueDatesModalProps {
	open: boolean;
	onClose: () => void;
	onApply: (params: {mode: ManageDueDatesMode; dueDate: Dayjs | null}) => void;
	isPending?: boolean;
}

export const ManageDueDatesModal: FC<ManageDueDatesModalProps> = ({
	open,
	onClose,
	onApply,
	isPending = false
}) => {
	const {classes, cx} = useStyles();
	const {t} = useSSTranslation();
	const [mode, setMode] = useState<ManageDueDatesMode>("set");
	const [dueDate, setDueDate] = useState<Dayjs | null>(null);

	useEffect(() => {
		if (!open) {
			setMode("set");
			setDueDate(null);
		}
	}, [open]);

	const handleApply = useCallback(() => {
		if (!open || isPending) {
			return;
		}
		if (mode === "set" && !dueDate) {
			return;
		}
		onApply({mode, dueDate});
	}, [open, isPending, mode, dueDate, onApply]);

	const applyDisabled = isPending || (mode === "set" && !dueDate);

	return (
		<Dialog
			open={open}
			onClose={(_event, reason) => {
				if (reason !== "backdropClick") {
					onClose();
				}
			}}
			aria-labelledby="manage-due-dates-dialog-title"
			aria-describedby="manage-due-dates-dialog-description"
			PaperProps={{
				sx: {
					bgcolor: "background.paperElevation3",
					borderColor: "background.paperElevation3"
				}
			}}
			maxWidth="sm"
			fullWidth
		>
			<DialogTitle id="manage-due-dates-dialog-title">
				{t("manage_due_dates_modal_title", "Manage Due Dates")}
				<Typography id="manage-due-dates-dialog-description" variant="body2" className={classes.subtitle}>
					{t("manage_due_dates_modal_subtitle", "Set or clear due dates")}
				</Typography>
			</DialogTitle>
			<DialogContent>
				<RadioGroup
					value={mode}
					onChange={(e) => setMode(e.target.value as ManageDueDatesMode)}
				>
					<Stack spacing={2}>
						<Box
							className={cx(classes.optionBox, mode === "set" && classes.optionBoxSelected)}
						>
							<FormControlLabel
								value="set"
								control={<Radio />}
								label={
									<Stack spacing={0.5} sx={{width: "100%"}}>
										<Typography variant="body1" className={classes.optionTitle}>
											{t("manage_due_dates_set_option_title", "Set Due Dates")}
										</Typography>
										<Typography variant="body2" className={classes.optionDescription}>
											{t(
												"manage_due_dates_set_option_description",
												"Set or update due dates for all selected records."
											)}
										</Typography>
									</Stack>
								}
								sx={{alignItems: "flex-start", m: 0, width: "100%"}}
							/>
							{mode === "set" && (
								<Box sx={{mt: 2, pl: 4}}>
									<CourseEnrollmentDueDateSection
										dueDate={dueDate}
										onDueDateChange={setDueDate}
										disabled={isPending}
										embedded
									/>
								</Box>
							)}
						</Box>

						<Box
							className={cx(classes.optionBox, mode === "clear" && classes.optionBoxSelected)}
						>
							<FormControlLabel
								value="clear"
								control={<Radio />}
								label={
									<Stack spacing={0.5}>
										<Typography variant="body1" className={classes.optionTitle}>
											{t("manage_due_dates_clear_option_title", "Clear All Due Dates")}
										</Typography>
										<Typography variant="body2" className={classes.optionDescription}>
											{t(
												"manage_due_dates_clear_option_description",
												"Clear due dates from all selected records."
											)}
										</Typography>
									</Stack>
								}
								sx={{alignItems: "flex-start", m: 0}}
							/>
							{mode === "clear" && (
								<Alert severity="error" variant="outlined" sx={{mt: 2}}>
									<AlertTitle>
										{t(
											"manage_due_dates_clear_warning",
											"This action cannot be undone."
										)}
									</AlertTitle>
								</Alert>
							)}
						</Box>
					</Stack>
				</RadioGroup>
			</DialogContent>
			<DialogActions>
				<Button variant="text" onClick={onClose} sx={buttonSx} disabled={isPending}>
					{t("team_progress_calendar_cancel", "Cancel")}
				</Button>
				<Button
					variant="text"
					onClick={handleApply}
					sx={buttonSx}
					disabled={applyDisabled}
					startIcon={isPending ? <CircularProgress size={16} /> : undefined}
				>
					{isPending
						? t("manage_due_dates_applying", "Applying...")
						: t("common_apply", "Apply")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
